import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FiCheck,
  FiShoppingBag,
  FiCreditCard,
  FiZap,
  FiAward,
  FiSettings,
  FiClock,
  FiArrowRight,
  FiStar,
  FiLayout,
} from 'react-icons/fi';
import {
  ecommercePackages,
  enterprisePackage,
  pricingNote,
} from '../data/pricingData';
import { trackEvent } from '../utils/analytics';

const iconMap = {
  bag: FiShoppingBag,
  card: FiCreditCard,
  zap: FiZap,
  award: FiAward,
  settings: FiSettings,
};

const WHATSAPP_NUMBER = '8801784996428';

const buildWhatsappLink = (pkg) => {
  const price = pkg.price
    ? ` (${pkg.currency}${pkg.price.toLocaleString('en-US')})`
    : '';
  const message = `Hi Durjoy! I'm interested in the *${pkg.name}*${price} ecommerce package. Can we discuss the details?`;
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
};

const trackPackageCta = (pkg, ctaLabel) =>
  trackEvent('pricing_cta_click', {
    package_name: pkg.name,
    package_price: pkg.price || 'quote',
    cta_label: ctaLabel,
  });

const getIcon = (key) => {
  const Icon = iconMap[key] || FiShoppingBag;
  return <Icon />;
};

const formatPrice = (price) => price.toLocaleString('en-US');

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

const PackageCard = ({ pkg }) => (
  <motion.div
    className={`package-card${pkg.popular ? ' popular' : ''}`}
    data-accent={pkg.accent}
    variants={cardVariants}
    whileHover={{ y: -6 }}
    transition={{ type: 'spring', stiffness: 280 }}
  >
    {pkg.popular && (
      <div className="package-popular-badge">
        <FiStar /> Most Popular
      </div>
    )}

    <div className="package-tab">
      <span className="package-tab-dot" />
      <span className="package-tab-name">{pkg.id}.config</span>
    </div>

    <div className="package-body">
      <div className="package-head">
        <div className="package-icon">{getIcon(pkg.icon)}</div>
        <div>
          <h3 className="package-name">{pkg.name}</h3>
          <p className="package-tagline">{pkg.tagline}</p>
        </div>
      </div>

      <div className="package-price">
        <span className="package-currency">{pkg.currency}</span>
        <span className="package-amount">{formatPrice(pkg.price)}</span>
        <span className="package-price-suffix">one-time</span>
      </div>

      <div className="package-timeline">
        <FiClock /> Delivery in {pkg.timeline}
      </div>

      <p className="package-description">{pkg.description}</p>

      {pkg.inherits && (
        <div className="package-inherits">
          Everything in{' '}
          <span className="syntax-function">{pkg.inherits}</span>{' '}
          <span className="syntax-keyword">+</span>
        </div>
      )}

      <ul className="package-features">
        {pkg.features.map((feature, i) => (
          <li key={i} className="package-feature">
            <FiCheck className="feature-check" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <a
        href={buildWhatsappLink(pkg)}
        target="_blank"
        rel="noopener noreferrer"
        className="package-cta"
        onClick={() => trackPackageCta(pkg, 'get_started')}
      >
        Get Started <FiArrowRight />
      </a>
    </div>
  </motion.div>
);

const Pricing = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Pricing & Packages | Durjoy';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <section className="pricing">
      <motion.div
        className="pricing-inner"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h2 className="section-comment" variants={itemVariants}>
          {'// pricing & packages'}
        </motion.h2>
        <motion.p className="section-subtitle" variants={itemVariants}>
          Transparent project pricing in Bangladeshi Taka. Pick a tier, or build
          your own with Custom Forge.
        </motion.p>

        <motion.div className="pricing-section-header" variants={itemVariants}>
          <h3 className="pricing-section-title">
            <span className="syntax-comment">{'/* '}</span>
            Ecommerce Packages
            <span className="syntax-comment">{' */'}</span>
          </h3>
          <p className="pricing-section-sub">
            From a simple COD-only shop to a full multi-vendor marketplace —
            tailored for businesses in Bangladesh.
          </p>
        </motion.div>

        <motion.div className="design-note" variants={itemVariants}>
          <div className="design-note-icon">
            <FiLayout />
          </div>
          <div className="design-note-text">
            <strong>Design is flexible.</strong> Share any reference design
            you like (Daraz, Pickaboo, Shopify themes, a Figma file — anything),
            or pick from a few options I'll suggest. The package price stays
            the same.
          </div>
        </motion.div>

        <motion.div className="package-grid" variants={containerVariants}>
          {ecommercePackages.map((pkg) => (
            <PackageCard key={pkg.id} pkg={pkg} />
          ))}
        </motion.div>

        <motion.div
          className="package-card enterprise"
          variants={cardVariants}
          whileHover={{ y: -4 }}
          transition={{ type: 'spring', stiffness: 280 }}
        >
          <div className="package-tab">
            <span className="package-tab-dot" />
            <span className="package-tab-name">{enterprisePackage.id}.config</span>
          </div>

          <div className="enterprise-body">
            <div className="enterprise-left">
              <div className="package-head">
                <div className="package-icon">{getIcon(enterprisePackage.icon)}</div>
                <div>
                  <h3 className="package-name">{enterprisePackage.name}</h3>
                  <p className="package-tagline">{enterprisePackage.tagline}</p>
                </div>
              </div>

              <div className="package-price enterprise-price">
                <span className="package-amount">Let's Talk</span>
                <span className="package-price-suffix">quote-based</span>
              </div>

              <p className="package-description">
                {enterprisePackage.description}
              </p>

              <a
                href={buildWhatsappLink(enterprisePackage)}
                target="_blank"
                rel="noopener noreferrer"
                className="package-cta"
                onClick={() =>
                  trackPackageCta(enterprisePackage, 'discuss_your_project')
                }
              >
                Discuss Your Project <FiArrowRight />
              </a>
            </div>

            <ul className="package-features enterprise-features">
              {enterprisePackage.features.map((feature, i) => (
                <li key={i} className="package-feature">
                  <FiCheck className="feature-check" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        <motion.div className="pricing-note" variants={itemVariants}>
          <p>{pricingNote.taxes}</p>
          <p>{pricingNote.payment}</p>
        </motion.div>

        <motion.div className="pricing-coming-soon" variants={itemVariants}>
          <span className="syntax-comment">
            {'// more packages coming soon — portfolio, POS, dashboards & more'}
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Pricing;
